/**
 * Раздел «График» (6.2 ТЗ): графики проектов и рабочее место планировщика — диаграмма Ганта
 * с таблицей, карточка работы, базовые планы и сравнение, проверка DCMA, параметры расчёта
 * и календари, обмен MS Project XML. Тот же вид — вкладка «График» в карточке проекта.
 */
import {
  can,
  canAnywhere,
  LEVEL_TITLES,
  LEVELS,
  type ScheduleSummary,
  type ScheduleView,
  seesMoney,
  WBS_KIND_TITLES,
  WBS_KINDS,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { Download, Plus, Redo2, Search, Undo2, Upload } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { plural } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { ActivityDialog } from './ActivityDialog';
import { ScheduleHolds } from './ActivityHolds';
import { BaselinesPanel } from './BaselinesPanel';
import { CheckPanel } from './CheckPanel';
import { ContractorsPanel } from './ContractorsPanel';
import { EvPanel } from './EvPanel';
import { FragmentDialog } from './FragmentDialog';
import { Gantt, useIsPhone } from './Gantt';
import { JournalPanel } from './JournalPanel';
import { LevelDialog } from './LevelDialog';
import { LField } from './LField';
import { LocationView } from './LocationView';
import { ManHoursPanel } from './ManHoursPanel';
import {
  buildRows,
  DEFAULT_FILTER,
  filterIds,
  fmtDur,
  fmtMoment,
  hpd,
  type RowFilter,
  ZOOMS,
  type ZoomKey,
} from './model';
import { NetworkView } from './Network';
import { PackagesPanel } from './PackagesPanel';
import { PortfolioView } from './PortfolioView';
import { PrintDialog } from './PrintDialog';
import { RiskPanel } from './RiskPanel';
import { ScenariosPanel } from './ScenariosPanel';
import { SettingsPanel } from './SettingsPanel';
import { type Focus, focusIds, TraceBar } from './TraceBar';
import { scheduleKey, useSchedule } from './useSchedule';
import { ViewsMenu } from './ViewsMenu';
import { WeekPlanPanel } from './WeekPlanPanel';

const PREF_KEY = 'epc:schedule-view';
function loadPrefs(): {
  zoom: ZoomKey;
  filter: RowFilter;
  baseline: boolean;
  links: boolean;
  view: 'gantt' | 'network' | 'location';
} {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return {
        zoom: p.zoom ?? 'weeks',
        filter: { ...DEFAULT_FILTER, ...p.filter, search: '' },
        baseline: p.baseline ?? true,
        links: p.links ?? true,
        view: p.view === 'network' || p.view === 'location' ? p.view : 'gantt',
      };
    }
  } catch {
    // без сохранённого вида
  }
  return { zoom: 'weeks', filter: DEFAULT_FILTER, baseline: true, links: true, view: 'gantt' };
}
function savePrefs(p: ReturnType<typeof loadPrefs>) {
  try {
    localStorage.setItem(PREF_KEY, JSON.stringify(p));
  } catch {
    // хранилище недоступно — вид не запоминается
  }
}

/* ───────────────────────── Список графиков ───────────────────────── */

export function SchedulesPage() {
  useCrumbs([]);
  const q = useQuery({
    queryKey: ['schedule-projects'],
    queryFn: () => api<ScheduleSummary[]>('/schedule/projects'),
  });
  return (
    <Page>
      <PageHeader
        title="График"
        subtitle="Графики проектов: собственный расчёт критического пути, базовые планы, проверка качества, обмен с MS Project"
      />
      {q.isPending ? <Spinner /> : q.isError ? <ErrorBox error={q.error} /> : <ScheduleList rows={q.data} />}
      {q.data && q.data.some((r) => r.scheduleId) ? <PortfolioView /> : null}
    </Page>
  );
}

function ScheduleList({ rows }: { rows: ScheduleSummary[] }) {
  if (!rows.length)
    return (
      <EmptyState title="Нет проектов с доступом к графику">
        Графики появятся здесь, когда вам откроют проект.
      </EmptyState>
    );
  return (
    <Panel bodyClassName="p-0">
      <ul className="divide-y divide-line md:hidden">
        {rows.map((r) => (
          <li key={r.projectId}>
            <Link
              to="/schedule/$projectId"
              params={{ projectId: r.projectId }}
              className="flex min-h-[64px] flex-col gap-1 px-4 py-3"
            >
              <span className="font-medium text-ink">
                {r.projectCode} <span className="font-normal text-ink-2">{r.projectName}</span>
              </span>
              <span className="text-[13px] text-ink-2">
                {r.scheduleId
                  ? `${plural(r.activities, 'работа', 'работы', 'работ')} · окончание ${fmtMoment(r.finish)}${r.slipDays ? ` · ${r.slipDays > 0 ? '+' : ''}${r.slipDays} дн к базовому` : ''}`
                  : 'График ещё не создан'}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <ScrollX className="max-md:hidden">
        <table className="w-full min-w-[900px] text-[14px]">
          <thead>
            <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
              <th className="py-2.5 pr-3 pl-5 font-medium">Проект</th>
              <th className="py-2.5 pr-3 text-right font-medium">Работ</th>
              <th className="py-2.5 pr-3 text-right font-medium">Критических</th>
              <th className="py-2.5 pr-3 font-medium">Начало</th>
              <th className="py-2.5 pr-3 font-medium">Окончание</th>
              <th className="py-2.5 pr-3 font-medium">К базовому плану</th>
              <th className="py-2.5 pr-3 text-right font-medium">Выполнено</th>
              <th className="py-2.5 pr-5 font-medium">Состояние</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.projectId} className="border-t border-line">
                <td className="py-2.5 pr-3 pl-5">
                  <Link
                    to="/schedule/$projectId"
                    params={{ projectId: r.projectId }}
                    className="font-medium text-ink hover:underline"
                  >
                    {r.projectCode}
                  </Link>
                  <div className="text-[13px] text-ink-2">{r.projectName}</div>
                </td>
                <td className="num py-2.5 pr-3 text-right">{r.scheduleId ? r.activities : '—'}</td>
                <td className="num py-2.5 pr-3 text-right">{r.scheduleId ? r.critical : '—'}</td>
                <td className="num py-2.5 pr-3">{fmtMoment(r.start)}</td>
                <td className="num py-2.5 pr-3">{fmtMoment(r.finish)}</td>
                <td className={clsx('num py-2.5 pr-3', r.slipDays && r.slipDays > 0 && 'text-danger')}>
                  {r.slipDays == null
                    ? '—'
                    : r.slipDays === 0
                      ? 'в срок'
                      : `${r.slipDays > 0 ? '+' : ''}${String(r.slipDays).replace('.', ',')} дн`}
                </td>
                <td className="num py-2.5 pr-3 text-right">
                  {r.progress == null ? '—' : `${String(r.progress).replace('.', ',')}%`}
                </td>
                <td className="py-2.5 pr-5">
                  {!r.scheduleId ? (
                    <Badge>не создан</Badge>
                  ) : r.status === 'approved' ? (
                    <Badge tone="success">утверждён</Badge>
                  ) : (
                    <Badge tone="neutral">в работе</Badge>
                  )}
                  {r.problems ? (
                    <Badge tone="warning" className="ml-1">
                      {plural(r.problems, 'замечание', 'замечания', 'замечаний')}
                    </Badge>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollX>
    </Panel>
  );
}

/* ───────────────────────── График проекта ───────────────────────── */

export function SchedulePage() {
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  return (
    <Page className="md:max-w-none">
      <ScheduleWorkspace projectId={projectId} standalone />
    </Page>
  );
}

/** Вкладка «График» в карточке проекта */
export function ScheduleTab({ project }: { project: { id: string } }) {
  return <ScheduleWorkspace projectId={project.id} />;
}

type Tab =
  | 'gantt'
  | 'week'
  | 'packages'
  | 'baselines'
  | 'check'
  | 'risk'
  | 'scenarios'
  | 'manhours'
  | 'ev'
  | 'contractors'
  | 'journal'
  | 'settings';

export function ScheduleWorkspace({ projectId, standalone }: { projectId: string; standalone?: boolean }) {
  const me = useMe();
  const s = useSchedule(projectId);
  const v = s.view;
  const proj = useQuery({
    queryKey: ['project-head', projectId],
    queryFn: () =>
      api<{
        id: string;
        code: string;
        name: string;
        legalEntityId: string;
        directionId: string | null;
        parentId: string | null;
        startDate: string | null;
      }>(`/projects/${projectId}`),
    enabled: !v && s.q.isSuccess,
  });
  const crumbTail = v
    ? [
        { label: v.project.code, title: v.project.name, to: `/schedule/${projectId}` },
        { label: v.schedule.name },
      ]
    : proj.data
      ? [{ label: proj.data.code, title: proj.data.name }]
      : null;
  useCrumbs(standalone ? crumbTail : undefined, s.q.error);
  // Ссылка из «Ждёт меня» открывает нужную вкладку: /schedule/<проект>?tab=week
  const [tab, setTab] = useState<Tab>(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      return t === 'week' || t === 'packages' || t === 'scenarios' || t === 'baselines' || t === 'contractors'
        ? t
        : 'gantt';
    } catch {
      return 'gantt';
    }
  });

  if (s.q.isPending) return <Spinner />;
  if (s.q.isError) return <ErrorBox error={s.q.error} />;
  if (!v) return <CreateSchedule projectId={projectId} project={proj.data ?? null} />;

  const crit = v.activities.filter((a) => a.dates?.critical).length;
  const done = v.activities.filter((a) => a.actualFinish).length;
  const h = hpd(v);
  // Человеко-часы — с фактом табеля: сотрудникам с правом видеть ресурсы
  const seeHours = Boolean(me.tenant?.staff) && canAnywhere(me.access, 'resources.view');
  // Освоенный объём — в деньгах: с уровнем денег «Акты»
  const seeEv = Boolean(me.tenant?.staff) && seesMoney(me.access, 'acts');
  const tabs: Array<{ key: Tab; title: string }> = [
    { key: 'gantt', title: 'Диаграмма Ганта' },
    { key: 'week', title: 'Недельный план' },
    { key: 'packages', title: 'Пакеты работ' },
    { key: 'baselines', title: `Базовые планы${v.baselines.length ? ` · ${v.baselines.length}` : ''}` },
    { key: 'check', title: 'Проверка DCMA' },
    { key: 'risk', title: 'Риски сроков' },
    { key: 'scenarios', title: 'Сценарии' },
    ...(seeHours ? [{ key: 'manhours' as const, title: 'Человеко-часы' }] : []),
    ...(seeEv ? [{ key: 'ev' as const, title: 'Освоенный объём' }] : []),
    ...(me.tenant?.staff ? [{ key: 'contractors' as const, title: 'Подрядчики' }] : []),
    { key: 'journal', title: 'Журнал правок' },
    { key: 'settings', title: 'Параметры и календари' },
  ];
  return (
    <div className="flex flex-col gap-4">
      {standalone ? (
        <PageHeader
          title={
            <span className="flex flex-wrap items-center gap-2">
              {v.schedule.name}
              {v.schedule.status === 'approved' ? (
                <Badge tone="success">утверждён</Badge>
              ) : (
                <Badge>в работе</Badge>
              )}
            </span>
          }
          subtitle={
            <Link to="/projects/$projectId" params={{ projectId }} className="hover:underline">
              {v.project.code} · {v.project.name}
            </Link>
          }
        />
      ) : null}
      <Stats
        items={[
          {
            label: 'Окончание по расчёту',
            value: fmtMoment(v.schedule.calcFinish),
            note: v.schedule.mustFinishBy ? `директивное ${fmtMoment(v.schedule.mustFinishBy)}` : undefined,
            tone:
              v.schedule.mustFinishBy &&
              v.schedule.calcFinish &&
              v.schedule.calcFinish > v.schedule.mustFinishBy
                ? 'danger'
                : 'default',
          },
          {
            label: 'Работ',
            value: v.activities.length,
            note: `выполнено ${done}, связей ${v.relations.length}`,
          },
          {
            label: 'Критических',
            value: crit,
            tone: crit ? 'danger' : 'default',
            note:
              v.schedule.settings.criticalBy === 'longest'
                ? 'по самой длинной цепочке'
                : `резерв ≤ ${fmtDur(v.schedule.settings.criticalFloatDays * h * 60, h)}`,
          },
          {
            label: 'Дата отчёта',
            value: fmtMoment(v.schedule.dataDate),
            note: v.schedule.calcMs != null ? `расчёт ${v.schedule.calcMs} мс` : undefined,
          },
        ]}
      />
      {v.schedule.conflicts.length || v.schedule.cycles.length ? <Conflicts view={v} /> : null}
      <div className="-mb-1 flex gap-1 overflow-x-auto border-b border-line" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={clsx(
              'relative min-h-11 shrink-0 px-3 text-[14px] font-medium whitespace-nowrap',
              tab === t.key ? 'text-ink' : 'text-ink-2 hover:text-ink',
            )}
          >
            {t.title}
            {tab === t.key ? <span className="absolute inset-x-2 -bottom-px h-0.5 bg-ink" /> : null}
          </button>
        ))}
      </div>
      {tab === 'gantt' ? <GanttArea s={s} view={v} projectId={projectId} /> : null}
      {tab === 'baselines' ? <BaselinesPanel view={v} onView={s.setServer} /> : null}
      {tab === 'check' ? <CheckPanel view={v} /> : null}
      {tab === 'risk' ? <RiskPanel view={v} /> : null}
      {tab === 'scenarios' ? <ScenariosPanel view={v} onView={s.setServer} /> : null}
      {tab === 'manhours' ? <ManHoursPanel view={v} /> : null}
      {tab === 'ev' ? <EvPanel view={v} /> : null}
      {tab === 'contractors' ? <ContractorsPanel view={v} /> : null}
      {tab === 'week' ? <WeekPlanPanel view={v} /> : null}
      {tab === 'packages' ? <PackagesPanel view={v} /> : null}
      {tab === 'journal' ? <JournalPanel view={v} onView={s.setServer} /> : null}
      {tab === 'settings' ? <SettingsPanel view={v} onView={s.setServer} /> : null}
    </div>
  );
}

function Conflicts({ view }: { view: ScheduleView }) {
  const [open, setOpen] = useState(false);
  const byId = new Map(view.activities.map((a) => [a.id, a]));
  const list = view.schedule.conflicts;
  return (
    <div className="rounded-lg border border-warning/40 bg-warning-soft px-4 py-3 text-[14px] text-ink">
      <button
        type="button"
        className="min-h-11 text-left font-medium"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        Замечания расчёта: {list.length}
        {view.schedule.cycles.length ? `, циклов связей: ${view.schedule.cycles.length}` : ''} —{' '}
        {open ? 'скрыть' : 'показать'}
      </button>
      {open ? (
        <ul className="mt-2 flex flex-col gap-1 text-[13px]">
          {list.slice(0, 50).map((c) => (
            <li key={`${c.activityId}:${c.kind}:${c.message}`}>
              <b>{byId.get(c.activityId)?.code ?? '—'}</b> {byId.get(c.activityId)?.name}: {c.message}
            </li>
          ))}
          {list.length > 50 ? <li>и ещё {list.length - 50}</li> : null}
        </ul>
      ) : null}
    </div>
  );
}

function CreateSchedule({
  projectId,
  project,
}: {
  projectId: string;
  project: {
    legalEntityId: string;
    directionId: string | null;
    parentId: string | null;
    startDate: string | null;
  } | null;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const allowed = project
    ? can(me.access, 'schedule.edit', {
        legalEntityId: project.legalEntityId,
        directionId: project.directionId,
        projectId,
        projectPath: [project.parentId],
      })
    : false;
  const [f, setF] = useState({
    startDate: project?.startDate ?? new Date().toISOString().slice(0, 10),
    from: 'empty',
    calendarPreset: '5x2',
  });
  const m = useMutation({
    mutationFn: () => api<ScheduleView>(`/projects/${projectId}/schedule`, { body: f }),
    onSuccess: (v) => {
      qc.setQueryData(scheduleKey(projectId), v);
      qc.invalidateQueries({ queryKey: ['schedule-projects'] });
      toast('График создан');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  return (
    <EmptyState title="График проекта ещё не создан">
      {allowed ? (
        <form
          className="mt-3 grid max-w-xl grid-cols-1 gap-4 sm:grid-cols-3"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            m.mutate();
          }}
        >
          <LField label="Начало работ">
            <Input
              type="date"
              required
              value={f.startDate}
              onChange={(e) => setF({ ...f, startDate: e.target.value })}
              className="max-md:h-11"
            />
          </LField>
          <LField label="Календарь">
            <Select
              value={f.calendarPreset}
              onChange={(e) => setF({ ...f, calendarPreset: e.target.value })}
              className="max-md:h-11"
            >
              <option value="5x2">5/2, 8 ч</option>
              <option value="6x1">6/1, 10 ч</option>
              <option value="5x2-2sh">5/2, две смены</option>
              <option value="7x24">Круглосуточно</option>
              <option value="vahta15">Вахта 15/15</option>
              <option value="vahta28">Вахта 28/28</option>
            </Select>
          </LField>
          <LField label="Начать с">
            <Select
              value={f.from}
              onChange={(e) => setF({ ...f, from: e.target.value })}
              className="max-md:h-11"
            >
              <option value="empty">Пустого графика</option>
              <option value="sample">Примера: камера, водовод, резервуар</option>
            </Select>
          </LField>
          <div className="sm:col-span-3">
            <Button variant="primary" type="submit" loading={m.isPending} className="max-md:h-11">
              Создать график
            </Button>
          </div>
          <p className="text-[13px] text-ink-2 sm:col-span-3">
            Готовый график из MS Project можно загрузить после создания: «Загрузить файл».
          </p>
        </form>
      ) : (
        'Создать график может планировщик или руководитель проекта.'
      )}
    </EmptyState>
  );
}

/* ───────────────────────── Диаграмма с панелью ───────────────────────── */

function GanttArea({
  s,
  view,
  projectId,
}: {
  s: ReturnType<typeof useSchedule>;
  view: ScheduleView;
  projectId: string;
}) {
  const phone = useIsPhone();
  const [prefs, setPrefsState] = useState(loadPrefs);
  const setPrefs = (p: Partial<typeof prefs>) =>
    setPrefsState((x) => {
      const n = { ...x, ...p, filter: { ...x.filter, ...(p.filter ?? {}) } };
      savePrefs(n);
      return n;
    });
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{
    id: string | null;
    wbsId?: string | null;
    afterId?: string | null;
  } | null>(null);
  const [wbsDialog, setWbsDialog] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [fragments, setFragments] = useState(false);
  const [level, setLevel] = useState(false);
  const [focus, setFocus] = useState<Focus | null>(null);
  // Трассировка идёт за выбором: нажали на работу в трассе — логика строится уже от неё
  const select = (id: string | null) => {
    setSelected(id);
    if (id) setFocus((f) => (f?.kind === 'trace' ? { kind: 'trace', trace: { ...f.trace, id } } : f));
  };
  const only = useMemo(() => focusIds(view, focus), [view, focus]);
  const rows = useMemo(
    () => buildRows(view, prefs.filter, collapsed, only),
    [view, prefs.filter, collapsed, only],
  );
  const netIds = useMemo(
    () => (prefs.view !== 'gantt' ? filterIds(view, prefs.filter, only) : null),
    [view, prefs.filter, only, prefs.view],
  );
  const canEdit = view.can.edit;
  const approved = view.schedule.status === 'approved';
  const sel = selected ? view.activities.find((a) => a.id === selected) : undefined;
  const toggle = (id: string) =>
    setCollapsed((c) => {
      const n = new Set(c);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const collapseAll = (to: boolean) => setCollapsed(to ? new Set(view.wbs.map((w) => w.id)) : new Set());

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {canEdit && !approved ? (
          <>
            <Button
              variant="primary"
              icon={<Plus className="size-4" />}
              onClick={() => setDialog({ id: null, wbsId: sel?.wbsId ?? null, afterId: sel?.id ?? null })}
              className="max-md:h-11"
            >
              Новая работа
            </Button>
            <Button onClick={() => setWbsDialog(true)} className="max-md:h-11">
              Добавить узел ИСР
            </Button>
            <Button onClick={() => setFragments(true)} className="max-md:h-11">
              Шаблоны фрагментов
            </Button>
            <Button onClick={() => setLevel(true)} className="max-md:h-11">
              Выровнять ресурсы
            </Button>
          </>
        ) : null}
        {canEdit ? (
          <span className="flex">
            <IconButton
              label={s.undoLabel ? `Отменить: ${s.undoLabel} (Ctrl+Z)` : 'Отменять нечего'}
              onClick={s.undo}
              disabled={!s.undoLabel}
              className="size-11 md:size-10"
            >
              <Undo2 className="size-4" />
            </IconButton>
            <IconButton
              label={s.redoLabel ? `Повторить: ${s.redoLabel} (Ctrl+Y)` : 'Повторять нечего'}
              onClick={s.redo}
              disabled={!s.redoLabel}
              className="size-11 md:size-10"
            >
              <Redo2 className="size-4" />
            </IconButton>
          </span>
        ) : null}
        <div className="relative flex min-w-[180px] flex-1 items-center md:max-w-[280px]">
          <Search className="pointer-events-none absolute left-3 size-4 text-ink-2" aria-hidden />
          <Input
            placeholder="Код или название"
            value={prefs.filter.search}
            onChange={(e) => setPrefs({ filter: { ...prefs.filter, search: e.target.value } })}
            className="pl-9 max-md:h-11"
            aria-label="Поиск работы"
          />
        </div>
        <div className="w-[calc(50%-4px)] sm:w-[200px]">
          <Select
            value={prefs.view}
            onChange={(e) => setPrefs({ view: e.target.value as 'gantt' | 'network' | 'location' })}
            className="max-md:h-11"
            aria-label="Вид"
          >
            <option value="gantt">Вид: диаграмма Ганта</option>
            <option value="network">Вид: сетевой график</option>
            <option value="location">Вид: время — местоположение</option>
          </Select>
        </div>
        <div className="w-[calc(50%-4px)] sm:w-[200px]">
          <Select
            value={prefs.zoom}
            onChange={(e) => setPrefs({ zoom: e.target.value as ZoomKey })}
            className="max-md:h-11"
            aria-label="Шкала"
          >
            {ZOOMS.map((z) => (
              <option key={z.key} value={z.key}>
                Шкала: {z.title.toLowerCase()}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-[calc(50%-4px)] sm:w-[200px]">
          <Select
            value={prefs.filter.groupBy}
            onChange={(e) => setPrefs({ filter: { ...prefs.filter, groupBy: e.target.value } })}
            className="max-md:h-11"
            aria-label="Группировка"
          >
            <option value="wbs">По ИСР</option>
            <option value="none">Без группировки</option>
            <option value="contractor">По подрядчику</option>
            <option value="zone">По захватке</option>
            <option value="responsible">По ответственному</option>
            <option value="workType">По виду работ</option>
            <option value="level">По уровню графика</option>
            {view.codeTypes.map((t) => (
              <option key={t.id} value={`code:${t.id}`}>
                По коду «{t.name}»
              </option>
            ))}
          </Select>
        </div>
        <div className="w-[calc(50%-4px)] sm:w-[200px]">
          <Select
            value={prefs.filter.level}
            onChange={(e) =>
              setPrefs({ filter: { ...prefs.filter, level: e.target.value as RowFilter['level'] } })
            }
            className="max-md:h-11"
            aria-label="Уровень графика"
          >
            <option value="all">Все уровни</option>
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {LEVEL_TITLES[l]}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-[calc(50%-4px)] sm:w-[200px]">
          <Select
            value={prefs.filter.sort}
            onChange={(e) =>
              setPrefs({ filter: { ...prefs.filter, sort: e.target.value as RowFilter['sort'] } })
            }
            className="max-md:h-11"
            aria-label="Сортировка"
          >
            <option value="tree">Порядок графика</option>
            <option value="es">По раннему началу</option>
            <option value="code">По коду</option>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <Checkbox
          className="min-h-11 items-center md:min-h-0"
          label="Только критические"
          checked={prefs.filter.criticalOnly}
          onChange={(e) => setPrefs({ filter: { ...prefs.filter, criticalOnly: e.target.checked } })}
        />
        <Checkbox
          className="min-h-11 items-center md:min-h-0"
          label="Базовый план"
          checked={prefs.baseline}
          onChange={(e) => setPrefs({ baseline: e.target.checked })}
        />
        <Checkbox
          className="min-h-11 items-center md:min-h-0"
          label="Связи"
          checked={prefs.links}
          onChange={(e) => setPrefs({ links: e.target.checked })}
        />
        {prefs.filter.groupBy === 'wbs' ? (
          <span className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => collapseAll(true)} className="max-md:h-11">
              Свернуть до ИСР
            </Button>
            <Button size="sm" variant="ghost" onClick={() => collapseAll(false)} className="max-md:h-11">
              Развернуть
            </Button>
          </span>
        ) : null}
        <span className="ml-auto flex flex-wrap gap-2">
          <ViewsMenu
            scheduleId={view.schedule.id}
            canShare={canEdit}
            current={prefs as unknown as Record<string, unknown>}
            onApply={(st) =>
              setPrefs({
                zoom: ZOOMS.some((z) => z.key === st.zoom) ? (st.zoom as ZoomKey) : prefs.zoom,
                view: st.view === 'network' || st.view === 'location' ? st.view : 'gantt',
                baseline: typeof st.baseline === 'boolean' ? st.baseline : prefs.baseline,
                links: typeof st.links === 'boolean' ? st.links : prefs.links,
                filter: {
                  ...DEFAULT_FILTER,
                  ...(typeof st.filter === 'object' && st.filter ? (st.filter as Partial<RowFilter>) : {}),
                  search: '',
                },
              })
            }
          />
          <ExportMenu scheduleId={view.schedule.id} />
          {canEdit && !approved ? (
            <Button
              icon={<Upload className="size-4" />}
              onClick={() => setImportOpen(true)}
              className="max-md:h-11"
            >
              Загрузить файл
            </Button>
          ) : null}
        </span>
      </div>
      {s.pending ? (
        <p className="text-[12px] text-ink-2" role="status">
          Сохраняется…
        </p>
      ) : null}
      {!view.activities.length ? (
        <EmptyState title="В графике пока нет работ">
          {canEdit
            ? 'Добавьте работы кнопкой «Новая работа» или загрузите график из MS Project.'
            : 'Работы появятся, когда планировщик их добавит.'}
        </EmptyState>
      ) : (
        <>
          <ScheduleHolds
            scheduleId={view.schedule.id}
            revision={view.schedule.revision}
            onOpen={(id) => setDialog({ id })}
          />
          <TraceBar view={view} selectedId={selected} focus={focus} onFocus={setFocus} />
          {prefs.view === 'location' ? (
            <LocationView
              view={view}
              only={netIds}
              selectedId={selected}
              onSelect={select}
              onOpen={(id) => setDialog({ id })}
              height={phone ? 'calc(100dvh - 260px)' : 'calc(100dvh - 330px)'}
            />
          ) : prefs.view === 'network' ? (
            <NetworkView
              view={view}
              only={netIds}
              selectedId={selected}
              onSelect={select}
              onOpen={(id) => setDialog({ id })}
              height={phone ? 'calc(100dvh - 260px)' : 'calc(100dvh - 330px)'}
            />
          ) : (
            <Gantt
              view={view}
              rows={rows}
              zoom={prefs.zoom}
              selectedId={selected}
              onSelect={select}
              onOpen={(id) => setDialog({ id })}
              onToggle={toggle}
              onEdit={(ops, label) => {
                s.commit(ops, label).catch(() => undefined);
                toast(label, 'info');
              }}
              canEdit={canEdit && !approved}
              showBaseline={prefs.baseline}
              showLinks={prefs.links}
              height={phone ? 'calc(100dvh - 260px)' : 'calc(100dvh - 330px)'}
            />
          )}
          {prefs.view === 'network' ? (
            <p className="text-[12px] text-ink-2">
              Сплошная линия — ведущая связь, пунктир — с резервом; красная рамка — критическая работа.
              Нажмите на блок — работа выбрана для трассировки; двойное нажатие откроет карточку.
            </p>
          ) : prefs.view === 'gantt' ? (
            <Legend />
          ) : null}
          {phone || prefs.view !== 'gantt' ? null : (
            <p className="text-[12px] text-ink-2">
              Перетащите полосу — работа получит ограничение «начало не ранее»; потяните правый край —
              изменится длительность; протяните кружок справа к другой работе — появится связь. Нажмите на
              название работы — откроется её карточка.
            </p>
          )}
        </>
      )}
      {dialog ? (
        <ActivityDialog
          key={dialog.id ?? 'new'}
          view={view}
          activityId={dialog.id}
          newIn={{ wbsId: dialog.wbsId ?? null, afterId: dialog.afterId ?? null }}
          onClose={() => setDialog(null)}
          onCommit={(ops, label) => {
            s.commit(ops, label).catch(() => undefined);
          }}
        />
      ) : null}
      {wbsDialog ? (
        <WbsDialog
          view={view}
          onClose={() => setWbsDialog(false)}
          onCommit={(ops, l) => s.commit(ops, l).catch(() => undefined)}
        />
      ) : null}
      {level ? <LevelDialog view={view} onClose={() => setLevel(false)} onView={s.setServer} /> : null}
      {fragments ? (
        <FragmentDialog
          view={view}
          selectedId={selected}
          onClose={() => setFragments(false)}
          onView={s.setServer}
        />
      ) : null}
      {importOpen ? (
        <ImportDialog
          view={view}
          projectId={projectId}
          onClose={() => setImportOpen(false)}
          onView={s.setServer}
        />
      ) : null}
    </div>
  );
}

function Legend() {
  const item = (el: React.ReactNode, label: string) => (
    <span className="inline-flex items-center gap-1.5">
      <svg width="22" height="12" aria-hidden>
        {el}
      </svg>
      {label}
    </span>
  );
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink-2">
      {item(<rect x="1" y="2" width="20" height="8" rx="2" fill="var(--color-ink-2)" />, 'работа')}
      {item(<rect x="1" y="2" width="20" height="8" rx="2" fill="var(--color-danger)" />, 'критическая')}
      {item(
        <rect x="1" y="2" width="20" height="8" rx="2" fill="var(--color-warning)" />,
        'почти критическая',
      )}
      {item(<rect x="1" y="7" width="20" height="4" fill="var(--color-line-strong)" />, 'базовый план')}
      {item(
        <path d="M6 0 l6 6 l-6 6 l-6 -6 Z" transform="translate(5 0)" fill="var(--color-ink-2)" />,
        'веха',
      )}
      {item(
        <line x1="1" x2="21" y1="6" y2="6" stroke="var(--color-line-strong)" strokeDasharray="2 3" />,
        'резерв',
      )}
      {item(
        <line
          x1="11"
          x2="11"
          y1="0"
          y2="12"
          stroke="var(--color-accent)"
          strokeWidth="2"
          strokeDasharray="3 2"
        />,
        'дата отчёта',
      )}
    </div>
  );
}

function WbsDialog({
  view,
  onClose,
  onCommit,
}: {
  view: ScheduleView;
  onClose: () => void;
  onCommit: (ops: import('@epc/contracts').ScheduleOp[], label: string) => void;
}) {
  const [f, setF] = useState({
    parentId: '',
    code: '',
    name: '',
    kind: 'other',
    objectId: '',
    sectionCode: '',
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Новый узел ИСР"
      description="Объект, раздел рабочего проекта, конструктив или этап — любой глубины"
      footer={
        <>
          <Button onClick={onClose} className="max-md:h-11">
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="wbs-form" className="max-md:h-11">
            Добавить узел
          </Button>
        </>
      }
    >
      <form
        id="wbs-form"
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!f.name.trim()) return;
          onCommit(
            [
              {
                op: 'wbs',
                id: crypto.randomUUID(),
                create: true,
                data: {
                  parentId: f.parentId || null,
                  code: f.code.trim() || null,
                  name: f.name.trim(),
                  kind: f.kind as (typeof WBS_KINDS)[number],
                  objectId: f.objectId || null,
                  sectionCode: f.sectionCode.trim() || null,
                  sortOrder: view.wbs.length,
                },
              },
            ],
            `Узел ИСР «${f.name.trim()}»`,
          );
          onClose();
        }}
      >
        <LField label="Внутри узла" className="sm:col-span-2">
          <Select
            value={f.parentId}
            onChange={(e) => setF({ ...f, parentId: e.target.value })}
            className="max-md:h-11"
          >
            <option value="">Верхний уровень</option>
            {view.wbs.map((w) => (
              <option key={w.id} value={w.id}>
                {w.code ? `${w.code} ` : ''}
                {w.name}
              </option>
            ))}
          </Select>
        </LField>
        <LField label="Код">
          <Input
            value={f.code}
            onChange={(e) => setF({ ...f, code: e.target.value })}
            maxLength={40}
            className="max-md:h-11"
          />
        </LField>
        <LField label="Вид">
          <Select
            value={f.kind}
            onChange={(e) => setF({ ...f, kind: e.target.value })}
            className="max-md:h-11"
          >
            {WBS_KINDS.map((k) => (
              <option key={k} value={k}>
                {WBS_KIND_TITLES[k]}
              </option>
            ))}
          </Select>
        </LField>
        <LField label="Название" className="sm:col-span-2">
          <Input
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            required
            maxLength={300}
            className="max-md:h-11"
          />
        </LField>
        {view.objects.length ? (
          <LField label="Объект проекта">
            <Select
              value={f.objectId}
              onChange={(e) => setF({ ...f, objectId: e.target.value })}
              className="max-md:h-11"
            >
              <option value="">Не связан</option>
              {view.objects.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          </LField>
        ) : null}
        <LField label="Шифр раздела РП" hint="Например, ВНС-1-КЖ">
          <Input
            value={f.sectionCode}
            onChange={(e) => setF({ ...f, sectionCode: e.target.value })}
            maxLength={80}
            className="max-md:h-11"
          />
        </LField>
      </form>
    </Dialog>
  );
}

function ImportDialog({
  view,
  projectId,
  onClose,
  onView,
}: {
  view: ScheduleView;
  projectId: string;
  onClose: () => void;
  onView: (v: ScheduleView) => void;
}) {
  const file = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const [mode, setMode] = useState<'replace' | 'append'>('replace');
  const [report, setReport] = useState<import('@epc/contracts').ImportReport | null>(null);
  // Сверка без записи: отчёт тот же, график не меняется (ПМ-03)
  const [checked, setChecked] = useState(false);
  const chk = useMutation({
    mutationFn: () => {
      const f = file.current?.files?.[0];
      if (!f) throw new Error('Выберите файл');
      const form = new FormData();
      form.append('file', f);
      return api<import('@epc/contracts').ImportReport>(`/schedules/${view.schedule.id}/import/check`, {
        form,
      });
    },
    onSuccess: (r) => {
      setChecked(true);
      setReport(r);
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const m = useMutation({
    // График из PDF загружается с отпечатком сверенного разбора: человек подтверждает то, что видел
    mutationFn: (confirmPdf?: string) => {
      const f = file.current?.files?.[0];
      if (!f) throw new Error('Выберите файл .xml');
      const form = new FormData();
      form.append('file', f);
      return api<{ report: import('@epc/contracts').ImportReport; view: ScheduleView }>(
        `/schedules/${view.schedule.id}/import`,
        { form, query: confirmPdf ? { mode, confirmPdf } : { mode } },
      );
    },
    onSuccess: (r) => {
      onView(r.view);
      setChecked(false);
      setReport(r.report);
      qc.invalidateQueries({ queryKey: ['schedule-projects'] });
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  void projectId;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Загрузка графика из файла"
      description="MS Project XML (Файл → Сохранить как → XML), Primavera P6 XER или P6 XML, Excel с колонками «Код», «Работа», «Длительность, дн», «Начало», «Окончание», «Предшественники». Импорт заканчивается отчётом о расхождениях дат файла с расчётом портала; «Сверить без загрузки» делает тот же отчёт, не меняя график."
      width="lg"
      footer={
        report && checked ? (
          <>
            <Button onClick={() => setReport(null)} className="max-md:h-11">
              Назад
            </Button>
            {report.pdfHash ? (
              <Button
                variant="primary"
                loading={m.isPending}
                onClick={() => m.mutate(report.pdfHash)}
                className="max-md:h-11"
              >
                Подтвердить и загрузить
              </Button>
            ) : (
              <Button variant="primary" onClick={onClose} className="max-md:h-11">
                Закрыть
              </Button>
            )}
          </>
        ) : report ? (
          <Button variant="primary" onClick={onClose} className="max-md:h-11">
            Готово
          </Button>
        ) : (
          <>
            <Button onClick={onClose} className="max-md:h-11">
              Отмена
            </Button>
            <Button
              loading={chk.isPending}
              disabled={m.isPending}
              onClick={() => chk.mutate()}
              className="max-md:h-11"
            >
              Сверить без загрузки
            </Button>
            <Button
              variant="primary"
              loading={m.isPending}
              disabled={chk.isPending}
              onClick={() => m.mutate(undefined)}
              className="max-md:h-11"
            >
              Загрузить
            </Button>
          </>
        )
      }
    >
      {report ? (
        <div className="flex flex-col gap-3 text-[14px]">
          <p>
            {checked ? 'Прочитано (график не менялся) из' : 'Загружено из'} «{report.format}»:{' '}
            {plural(report.activities, 'работа', 'работы', 'работ')},{' '}
            {plural(report.relations, 'связь', 'связи', 'связей')}, узлов ИСР — {report.wbs}, календарей —{' '}
            {report.calendars}.
          </p>
          <p>
            Сверено с датами файла: {plural(report.compared, 'работа', 'работы', 'работ')}.{' '}
            {report.mismatches.length ? (
              <b className="text-warning">Расхождений: {report.mismatches.length}</b>
            ) : (
              <b className="text-success">Расхождений нет — даты совпали.</b>
            )}
          </p>
          {report.critical && report.critical.compared > 0 ? (
            <p>
              Критический путь (резерв не больше нуля): в файле {report.critical.file}, в портале{' '}
              {report.critical.portal}.{' '}
              {report.critical.onlyFile.length || report.critical.onlyPortal.length ? (
                <b className="text-warning">
                  Только в файле: {report.critical.onlyFile.slice(0, 15).join(', ') || '—'}; только в портале:{' '}
                  {report.critical.onlyPortal.slice(0, 15).join(', ') || '—'}.
                </b>
              ) : (
                <b className="text-success">Совпал.</b>
              )}
            </p>
          ) : null}
          {report.fileFinish && report.portalFinish ? (
            <p>
              Окончание проекта: в файле {fmtMoment(report.fileFinish, true)}, в портале{' '}
              {fmtMoment(report.portalFinish, true)}.
            </p>
          ) : null}
          {report.mismatches.length ? (
            <ScrollX>
              <table className="w-full min-w-[560px] text-[13px]">
                <thead>
                  <tr className="text-left text-ink-2">
                    <th className="py-1 pr-2">Работа</th>
                    <th className="py-1 pr-2">Что</th>
                    <th className="py-1 pr-2">В файле</th>
                    <th className="py-1">В портале</th>
                  </tr>
                </thead>
                <tbody>
                  {report.mismatches.slice(0, 200).map((x) => (
                    <tr key={`${x.code}:${x.field}`} className="border-t border-line">
                      <td className="py-1 pr-2">
                        {x.code} {x.name}
                      </td>
                      <td className="py-1 pr-2">{x.field}</td>
                      <td className="num py-1 pr-2">
                        {x.file.includes('T') ? fmtMoment(x.file, true) : x.file}
                      </td>
                      <td className="num py-1">
                        {x.portal.includes('T') ? fmtMoment(x.portal, true) : x.portal}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          ) : null}
          {report.warnings.length ? (
            <ul className="list-disc pl-5 text-[13px] text-ink-2">
              {report.warnings.slice(0, 30).map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {/* Форма не убирается из страницы при показе отчёта: после «Назад» выбранный файл остаётся */}
      <div className={report ? 'hidden' : 'flex flex-col gap-4'}>
        <input
          ref={file}
          type="file"
          accept=".xml,.xer,.xlsx,.pdf,application/pdf,application/xml,text/xml,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="text-[14px]"
          aria-label="Файл графика: MS Project XML, Primavera XER, P6 XML, Excel или PDF"
        />
        <fieldset className="flex flex-col gap-2">
          <label className="flex min-h-11 items-center gap-2 text-[14px]">
            <input
              type="radio"
              name="mode"
              checked={mode === 'replace'}
              onChange={() => setMode('replace')}
            />{' '}
            Заменить работы графика ({view.activities.length})
          </label>
          <label className="flex min-h-11 items-center gap-2 text-[14px]">
            <input type="radio" name="mode" checked={mode === 'append'} onChange={() => setMode('append')} />{' '}
            Добавить к графику
          </label>
        </fieldset>
        <p className="text-[13px] text-ink-2">
          Файл MPP сохраните в MS Project как XML. Если в XER несколько проектов, загружается первый.
        </p>
      </div>
    </Dialog>
  );
}

/** Показатели графика: на телефоне — два в ряд */
function Stats({
  items,
}: {
  items: Array<{ label: string; value: React.ReactNode; note?: string; tone?: 'default' | 'danger' }>;
}) {
  return (
    <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-line bg-surface lg:grid-cols-4">
      {items.map((it, i) => (
        <div
          key={it.label}
          className={clsx(
            'flex min-w-0 flex-col gap-0.5 border-line px-4 py-3 md:px-5 md:py-4',
            i % 2 === 1 && 'border-l',
            i >= 2 && 'border-t lg:border-t-0',
            i === 2 && 'lg:border-l',
          )}
        >
          <span className="text-[12px] text-ink-2 md:text-[13px]">{it.label}</span>
          <span
            className={clsx(
              'num text-[20px] leading-7 font-semibold md:text-[26px] md:leading-8',
              it.tone === 'danger' ? 'text-danger' : 'text-ink',
            )}
          >
            {it.value}
          </span>
          {it.note ? (
            <span className="text-[12px] leading-4 text-ink-2 md:text-[13px]">{it.note}</span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Выгрузка графика: MS Project XML, Primavera P6 XER и P6 XML (ПЛ-33) */
function ExportMenu({ scheduleId }: { scheduleId: string }) {
  const [open, setOpen] = useState(false);
  const [print, setPrint] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  // Меню закрывается щелчком мимо и клавишей Esc
  useEffect(() => {
    if (!open) return;
    const click = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', click);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', click);
      document.removeEventListener('keydown', key);
    };
  }, [open]);
  const items = [
    ['export.xml', 'MS Project XML'],
    ['export.xer', 'Primavera P6 XER'],
    ['export.p6.xml', 'Primavera P6 XML'],
    ['export.xlsx', 'Excel'],
  ];
  return (
    <span className="relative" ref={box}>
      <Button
        icon={<Download className="size-4" />}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="max-md:h-11"
      >
        Скачать график
      </Button>
      {open ? (
        <span className="absolute right-0 z-30 mt-1 flex w-56 flex-col rounded-md border border-line bg-surface py-1 shadow-md">
          {items.map(([path, title]) => (
            <a
              key={path}
              href={`/api/v1/schedules/${scheduleId}/${path}`}
              download
              onClick={() => setOpen(false)}
              className="flex min-h-11 items-center px-4 text-[14px] text-ink hover:bg-surface-2"
            >
              {title}
            </a>
          ))}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setPrint(true);
            }}
            className="flex min-h-11 items-center px-4 text-left text-[14px] text-ink hover:bg-surface-2"
          >
            PDF для печати…
          </button>
        </span>
      ) : null}
      {print ? <PrintDialog scheduleId={scheduleId} onClose={() => setPrint(false)} /> : null}
    </span>
  );
}
